import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { unwrapApiResult } from 'js/utils/api';
import { unwrapQuestionScoresResult } from 'js/store/reducers/queries';
import { LoadingMessage } from 'js/components';
import SeriesRadarChart, {
  seriesItemsFromLegendMaps,
} from 'js/components/Charts/SeriesRadarChart';
import { getCategoryLookups } from 'js/utils/question-set';
import standardCohortKeys from 'js/utils/cohort-keys';
import {
  COMBINED_CHART_PALETTE_INDEX,
  cohortChartPaletteIndex,
} from 'js/utils/color';
import { listBreakouts } from 'js/actions/breakout-actions';
import {
  lookupQuestionScores,
  lookupOrgRubricScores,
} from 'js/actions/org-actions';
import {
  rubricTeamsToQuestionScores,
  collectRubricAssessmentKeys,
  filterInheritedBreakouts,
} from 'js/utils/rubric-org-scores';
import { sortCohortLegendItems, NONE_LABEL } from 'js/utils/assessment-cohorts';
import {
  omitEmptyNoneFromRadarLegend,
  disableEmptyNamedRadarLegendFields,
  cohortSliceHasNumericScores,
} from 'js/engines/SurveyAssessment/components/Review/assessment-radar-series';
import { scoreNode } from '../OrgRecommendations/utils';
import { buildOrgHistoryDataMap } from '../OrgAssessmentHistory/buildOrgHistoryDataMap';
import {
  flattenOrgPickerRows,
  filterQuestionScoresByPickerRow,
} from 'js/components/OrgNodePicker/flattenOrgPickerRows';
import OrgNodePicker from 'js/components/OrgNodePicker/OrgNodePicker';
import orgPickerStyles from 'js/components/OrgNodePicker/OrgNodePicker.module.scss';
import BreakoutPicker from './BreakoutPicker';

import styles from '../OrgAssessmentHistory/OrgAssessmentHistory.module.scss';

function scoresForCohort(questionScores, cohortKey) {
  return (questionScores ?? []).map((team) => ({
    ...team,
    scores:
      team.by_cohort?.[cohortKey] ??
      (cohortKey === standardCohortKeys.CUMULATIVE ? team.scores : []),
  }));
}

function OrgAssessmentRadarChart({
  organization,
  assessmentTemplate,
  questionScores,
  catalog = null,
  orgRubricScores,
  assessmentKey,
  orgId,
  isRubric = false,
  listBreakouts,
  lookupQuestionScores,
  lookupOrgRubricScores,
  lookupBreakoutScores = null,
}) {
  const pickerRows = useMemo(
    () => flattenOrgPickerRows(organization),
    [organization]
  );
  const [selectedRow, setSelectedRow] = useState(null);
  const [breakoutId, setBreakoutId] = useState('');
  const [breakouts, setBreakouts] = useState([]);
  const [breakoutQuestionScores, setBreakoutQuestionScores] = useState(null);
  const [loadingBreakout, setLoadingBreakout] = useState(false);
  const [pinnedCohort, setPinnedCohort] = useState(null);
  const [peekedCohort, setPeekedCohort] = useState(null);
  const lastActiveScoresRef = useRef(null);

  useEffect(() => {
    if (!pickerRows.length) {
      setSelectedRow(null);
      return;
    }
    setSelectedRow((current) => {
      if (current && pickerRows.some((row) => row.rowKey === current.rowKey)) {
        return current;
      }
      return pickerRows[0];
    });
  }, [pickerRows]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (isRubric) {
        const document = orgRubricScores?.document;
        const keys = collectRubricAssessmentKeys(document);
        if (keys.length === 0) {
          if (!cancelled) setBreakouts([]);
          return;
        }
        const result = await listBreakouts(null, keys);
        const rows = unwrapApiResult(result) ?? [];
        if (!cancelled) {
          setBreakouts(filterInheritedBreakouts(rows, document));
        }
        return;
      }
      const result = await listBreakouts(assessmentKey);
      const rows = unwrapApiResult(result) ?? [];
      if (!cancelled) setBreakouts(rows);
    };
    load().catch(() => {
      if (!cancelled) setBreakouts([]);
    });
    return () => {
      cancelled = true;
    };
  }, [assessmentKey, isRubric, listBreakouts, orgRubricScores?.document]);

  useEffect(() => {
    if (!breakoutId || !orgId || !assessmentKey) {
      setLoadingBreakout(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingBreakout(true);
    const req = lookupBreakoutScores
      ? lookupBreakoutScores(breakoutId)
      : isRubric
      ? lookupOrgRubricScores(orgId, assessmentKey, { breakout_id: breakoutId })
      : lookupQuestionScores(orgId, assessmentKey, { breakout_id: breakoutId });
    req
      .then((result) => {
        if (cancelled) return;
        const payload = unwrapApiResult(result);
        if (payload?.document && payload?.teams) {
          const { teams, document } = payload;
          const base = rubricTeamsToQuestionScores(teams, document);
          setBreakoutQuestionScores(
            base.map((row, index) => {
              const team = teams[index];
              const byCohort = {};
              Object.entries(team.by_cohort ?? {}).forEach(([key, slice]) => {
                const fake = [
                  { ...team, evaluation: slice.evaluation ?? slice },
                ];
                byCohort[key] =
                  rubricTeamsToQuestionScores(fake, document)[0]?.scores ?? [];
              });
              return { ...row, by_cohort: byCohort };
            })
          );
        } else {
          setBreakoutQuestionScores(unwrapQuestionScoresResult(payload).teams);
        }
      })
      .catch(() => {
        if (!cancelled) setBreakoutQuestionScores(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingBreakout(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    breakoutId,
    orgId,
    assessmentKey,
    isRubric,
    lookupOrgRubricScores,
    lookupQuestionScores,
    lookupBreakoutScores,
  ]);

  const activeQuestionScores = useMemo(() => {
    if (!breakoutId) {
      return questionScores;
    }
    if (breakoutQuestionScores) {
      return breakoutQuestionScores;
    }
    if (loadingBreakout) {
      return lastActiveScoresRef.current ?? questionScores;
    }
    return null;
  }, [breakoutId, breakoutQuestionScores, loadingBreakout, questionScores]);

  useEffect(() => {
    if (activeQuestionScores) {
      lastActiveScoresRef.current = activeQuestionScores;
    }
  }, [activeQuestionScores]);

  const legendFields = useMemo(() => {
    const combined = {
      key: standardCohortKeys.CUMULATIVE,
      paletteIndex: COMBINED_CHART_PALETTE_INDEX,
      name: '(Combined)',
    };
    if (!breakoutId || !breakoutQuestionScores) {
      return [combined];
    }
    const keys = new Set();
    const scopedScores = filterQuestionScoresByPickerRow(
      breakoutQuestionScores,
      selectedRow
    );
    scopedScores.forEach((team) => {
      Object.keys(team.by_cohort ?? {}).forEach((key) => keys.add(key));
    });
    keys.delete(standardCohortKeys.CUMULATIVE);
    if (
      keys.has(standardCohortKeys.NULL) &&
      !cohortSliceHasNumericScores(scopedScores, standardCohortKeys.NULL)
    ) {
      keys.delete(standardCohortKeys.NULL);
    }
    const rest = sortCohortLegendItems(
      [...keys].map((key) => ({
        key,
        name: key === standardCohortKeys.NULL ? NONE_LABEL : key,
      }))
    ).map((item, i) => ({
      key: item.key,
      paletteIndex: cohortChartPaletteIndex(i),
      name: item.name,
    }));
    return [combined, ...rest];
  }, [breakoutId, breakoutQuestionScores, selectedRow]);

  const seriesDataMaps = useMemo(() => {
    if (!selectedRow || !activeQuestionScores) {
      return null;
    }
    if (!assessmentTemplate && !catalog) {
      return null;
    }
    const maps = {};
    legendFields.forEach((field) => {
      const scored = scoreNode(
        selectedRow.node,
        scoresForCohort(activeQuestionScores, field.key),
        organization
      );
      maps[field.key] = buildOrgHistoryDataMap(
        assessmentTemplate,
        scored,
        catalog
      );
    });
    return maps;
  }, [
    selectedRow,
    assessmentTemplate,
    activeQuestionScores,
    legendFields,
    organization,
    catalog,
  ]);

  const visibleLegendFields = useMemo(() => {
    const withoutEmptyNone = omitEmptyNoneFromRadarLegend(
      legendFields,
      seriesDataMaps
    );
    return disableEmptyNamedRadarLegendFields(withoutEmptyNone, seriesDataMaps);
  }, [legendFields, seriesDataMaps]);

  useEffect(() => {
    const disabledKeys = new Set(
      visibleLegendFields
        .filter((field) => field.disabled)
        .map((field) => field.key)
    );
    if (disabledKeys.has(pinnedCohort)) {
      setPinnedCohort(null);
    }
    if (disabledKeys.has(peekedCohort)) {
      setPeekedCohort(null);
    }
  }, [visibleLegendFields, pinnedCohort, peekedCohort]);

  const dataMap = seriesDataMaps?.[standardCohortKeys.CUMULATIVE] ?? null;
  const showInitialLoading = loadingBreakout && !dataMap;

  const categoryLookups = useMemo(() => {
    if (!dataMap) {
      return new Map();
    }
    return getCategoryLookups(
      [{ config: assessmentTemplate?.config }],
      dataMap.categories
    );
  }, [dataMap, assessmentTemplate?.config]);

  const categoryColorMap = useMemo(() => {
    const map = {};
    categoryLookups.forEach((value, key) => {
      map[key] = value.palette;
    });
    return map;
  }, [categoryLookups]);

  const selectedBreakout = breakouts.find((b) => String(b.id) === breakoutId);
  const handlePin = useCallback((key) => setPinnedCohort(key), []);
  const handleClearPin = useCallback(() => setPinnedCohort(null), []);
  const handlePeek = useCallback((key) => setPeekedCohort(key), []);
  const handleClearPeek = useCallback(() => setPeekedCohort(null), []);

  return (
    <div className={styles.root}>
      <div className={classNames('manage-org', styles['picker-shell'])}>
        <OrgNodePicker
          className={orgPickerStyles.compact}
          rows={pickerRows}
          selectedRow={selectedRow}
          onSelect={setSelectedRow}
          disabled={!organization}
        />
        {breakouts.length > 0 ? (
          <BreakoutPicker
            breakouts={breakouts}
            selectedId={breakoutId}
            onSelect={(id) => {
              setBreakoutId(id);
              setPinnedCohort(null);
              setPeekedCohort(null);
            }}
            disabled={loadingBreakout}
          />
        ) : null}
      </div>
      {showInitialLoading ? (
        <LoadingMessage iconSize="2x">Loading data&hellip;</LoadingMessage>
      ) : dataMap ? (
        <SeriesRadarChart
          questionSets={dataMap.questionSets ?? {}}
          series={seriesItemsFromLegendMaps(
            visibleLegendFields,
            seriesDataMaps
          )}
          categoryLookups={categoryLookups}
          categoryColorMap={categoryColorMap}
          legendHeading="Cohorts"
          legendCaption={selectedBreakout?.name ?? null}
          isLoading={loadingBreakout}
          pinnedKey={pinnedCohort}
          activeKey={peekedCohort ?? pinnedCohort}
          onPin={handlePin}
          onClearPin={handleClearPin}
          onPeek={handlePeek}
          onClearPeek={handleClearPeek}
          enableSeriesFilter
          dimInactive
        />
      ) : null}
    </div>
  );
}

export default connect(null, {
  listBreakouts,
  lookupQuestionScores,
  lookupOrgRubricScores,
})(OrgAssessmentRadarChart);
